import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { Code, Layout, Eye, Wand2, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import * as prettier from 'prettier/standalone';
import * as htmlParser from 'prettier/plugins/html';

import { findElementInCode } from '../../utils/codeNavigation';
import { Button } from '../ui/button';

import LivePreview from './LivePreview';

// Lazy load Monaco Editor to reduce initial bundle size
const MonacoEditor = lazy(() => import('@monaco-editor/react'));

/**
 * SplitViewEditor Component
 *
 * Main code editor with three view modes: Code, Split, Preview
 * Features:
 * - Monaco Editor with HTML syntax highlighting
 * - Live preview in iframe
 * - Click-to-code navigation
 * - HTML formatting with Prettier
 * - Auto-save detection
 */
const SplitViewEditor = ({ value, onChange, isDirty, onFormat }) => {
  const [viewMode, setViewMode] = useState('split'); // 'code' | 'split' | 'preview'
  const [content, setContent] = useState(value || '');
  const [previewHtml, setPreviewHtml] = useState(value || '');
  const [isRendering, setIsRendering] = useState(false);
  const [formatError, setFormatError] = useState(null);
  const editorRef = useRef(null);
  const formatTimeoutRef = useRef(null);

  // Debounce preview updates (500ms)
  useEffect(() => {
    setIsRendering(true);
    const timer = setTimeout(() => {
      setPreviewHtml(content);
      setIsRendering(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [content]);

  // Update content when value prop changes
  useEffect(() => {
    if (value !== content) {
      setContent(value);
    }
  }, [value, content]);

  // Handle editor change
  const handleEditorChange = (newValue) => {
    setContent(newValue || '');
    onChange(newValue || '');
  };

  // Handle Monaco editor mount
  const handleEditorMount = (editor) => {
    editorRef.current = editor;

    // Focus editor after mount
    editor.focus();
  };

  // Navigate to line in code based on preview click
  const handleNavigateToCode = (elementData) => {
    if (!editorRef.current) return;

    const lineNumber = findElementInCode(content, elementData);

    if (lineNumber > 0) {
      // Reveal line in center of editor
      editorRef.current.revealLineInCenter(lineNumber);

      // Set cursor position
      editorRef.current.setPosition({
        lineNumber,
        column: 1
      });

      // Focus editor
      editorRef.current.focus();

      // Add temporary highlight
      const decorations = editorRef.current.deltaDecorations([], [
        {
          range: {
            startLineNumber: lineNumber,
            startColumn: 1,
            endLineNumber: lineNumber,
            endColumn: 1000
          },
          options: {
            isWholeLine: true,
            className: 'line-highlight',
            glyphMarginClassName: 'line-highlight-glyph'
          }
        }
      ]);

      // Remove highlight after 2 seconds
      setTimeout(() => {
        editorRef.current.deltaDecorations(decorations, []);
      }, 2000);
    }
  };

  // Format HTML with Prettier
  const handleFormatHtml = async () => {
    setFormatError(null);

    try {
      const formatted = await prettier.format(content, {
        parser: 'html',
        plugins: [htmlParser],
        printWidth: 80,
        tabWidth: 2,
        useTabs: false,
        htmlWhitespaceSensitivity: 'ignore'
      });

      setContent(formatted);
      onChange(formatted);

      // Show success message
      if (onFormat) onFormat();

      // Clear any previous error after successful format
      if (formatTimeoutRef.current) {
        clearTimeout(formatTimeoutRef.current);
      }
    } catch (error) {
      console.error('Format error:', error);
      setFormatError('Failed to format HTML. Please check your syntax.');

      // Clear error after 5 seconds
      formatTimeoutRef.current = setTimeout(() => {
        setFormatError(null);
      }, 5000);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b bg-gray-50 dark:bg-gray-800 p-2">
        <div className="flex items-center space-x-1">
          {/* View Mode Buttons */}
          <Button
            size="sm"
            variant={viewMode === 'code' ? 'default' : 'outline'}
            onClick={() => setViewMode('code')}
            className={viewMode === 'code' ? 'bg-gradient-to-r from-red-800 to-red-900 text-amber-50 border border-amber-600/30' : ''}
          >
            <Code className="h-4 w-4 mr-1" />
            Code
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'split' ? 'default' : 'outline'}
            onClick={() => setViewMode('split')}
            className={viewMode === 'split' ? 'bg-gradient-to-r from-red-800 to-red-900 text-amber-50 border border-amber-600/30' : ''}
          >
            <Layout className="h-4 w-4 mr-1" />
            Split
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'preview' ? 'default' : 'outline'}
            onClick={() => setViewMode('preview')}
            className={viewMode === 'preview' ? 'bg-gradient-to-r from-red-800 to-red-900 text-amber-50 border border-amber-600/30' : ''}
          >
            <Eye className="h-4 w-4 mr-1" />
            Preview
          </Button>
        </div>

        <div className="flex items-center space-x-2">
          {/* Format Button */}
          <Button
            size="sm"
            variant="outline"
            onClick={handleFormatHtml}
          >
            <Wand2 className="h-4 w-4 mr-1" />
            Format HTML
          </Button>

          {/* Draft Status */}
          <div className="flex items-center text-sm">
            {isDirty ? (
              <span className="flex items-center text-orange-600">
                <AlertCircle className="h-4 w-4 mr-1" />
                Unsaved
              </span>
            ) : (
              <span className="flex items-center text-green-600">
                <CheckCircle className="h-4 w-4 mr-1" />
                Saved
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Format Error Message */}
      {formatError && (
        <div className="bg-red-50 border-b border-red-200 text-red-700 px-4 py-2 text-sm">
          {formatError}
        </div>
      )}

      {/* Editor/Preview Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Code View */}
        {(viewMode === 'code' || viewMode === 'split') && (
          <div className={viewMode === 'split' ? 'w-1/2 border-r' : 'w-full'}>
            <Suspense
              fallback={
                <div className="flex items-center justify-center h-full bg-[#1e1e1e]">
                  <div className="text-white flex items-center space-x-2">
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Loading editor...</span>
                  </div>
                </div>
              }
            >
              <MonacoEditor
                height="100%"
                defaultLanguage="html"
                value={content}
                onChange={handleEditorChange}
                onMount={handleEditorMount}
                theme="vs-dark"
                options={{
                  automaticLayout: true,
                  wordWrap: 'on',
                  minimap: { enabled: false },
                  fontSize: 14,
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  tabSize: 2,
                  insertSpaces: true,
                  formatOnPaste: true,
                  formatOnType: true,
                }}
              />
            </Suspense>

            {/* Add custom CSS for line highlighting */}
            <style>{`
              .line-highlight {
                background: rgba(255, 255, 255, 0.1);
                animation: highlight-fade 2s ease-out;
              }

              .line-highlight-glyph {
                background: rgba(220, 38, 38, 0.5);
              }

              @keyframes highlight-fade {
                0% {
                  background: rgba(220, 38, 38, 0.3);
                }
                100% {
                  background: transparent;
                }
              }
            `}</style>
          </div>
        )}

        {/* Preview View */}
        {(viewMode === 'preview' || viewMode === 'split') && (
          <div className={viewMode === 'split' ? 'w-1/2' : 'w-full'}>
            <LivePreview
              html={previewHtml}
              onNavigate={handleNavigateToCode}
              isRendering={isRendering}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SplitViewEditor;
